const { getElements } = window.BAO.Utils

/**
 * Sections can share an anchor id when the same content is split into a desktop
 * copy and a mobile copy (see the "Display" setting on
 * section-text-image-overlay) — only one of the pair has a box at any given
 * breakpoint, the other is `display: none`.
 *
 * Duplicate ids mean the browser always jumps to whichever copy comes first in
 * the DOM, which is often the hidden one. This re-scrolls to the copy that is
 * actually visible.
 *
 * It only ever acts when a hash matches more than one element, so single-target
 * anchors (including the drawer-opening ones like #gappt-open) are untouched.
 */
class AnchorScroll {
  constructor () {
    this.listeners = new window.BAO.Listeners()

    /**
     * Whether the visitor has taken over scrolling, in which case we stop
     * correcting their position.
     *
     * @type {boolean}
     * @private
     */
    this._visitorHasScrolled = false
  }

  /**
   * @returns {undefined}
   */
  init () {
    this.setupListeners()

    if (document.readyState === 'loading') {
      this.listeners.add(
        document,
        'DOMContentLoaded',
        this.correctInitialScroll.bind(this),
      )

      return
    }

    this.correctInitialScroll()
  }

  /**
   * @returns {undefined}
   */
  setupListeners () {
    ;['wheel', 'touchstart', 'keydown'].forEach(event => {
      this.listeners.add(window, event, this.markScrolled.bind(this), {
        passive: true,
        once: true,
      })
    })

    // Images and async sections settle after DOMContentLoaded and shift the
    // target, so correct again once everything has loaded.
    this.listeners.add(window, 'load', this.correctInitialScroll.bind(this))

    this.listeners.add(window, 'hashchange', this.handleHashChange.bind(this))
    this.listeners.add(document, 'click', this.handleClick.bind(this))
  }

  /**
   * @returns {undefined}
   */
  markScrolled () {
    this._visitorHasScrolled = true
  }

  /**
   * Landing on the page with a hash. Instant, so we're correcting the browser's
   * jump rather than animating on top of it.
   *
   * @returns {undefined}
   */
  correctInitialScroll () {
    if (this._visitorHasScrolled) return

    this.scrollToVisibleTarget(window.location.hash, 'auto')
  }

  /**
   * @returns {undefined}
   */
  handleHashChange () {
    this.scrollToVisibleTarget(window.location.hash, 'smooth')
  }

  /**
   * Clicking a link to the hash that's already in the URL fires no hashchange,
   * so we correct off the click too. We don't preventDefault — the browser's
   * own jump keeps `:target` and focus behaviour intact, we just follow it up.
   *
   * @param {MouseEvent} e
   *
   * @returns {undefined}
   */
  handleClick (e) {
    const link = e.target.closest?.('a[href]')

    if (!link || !link.hash || link.hash === '#') return
    if (link.host !== window.location.host) return
    if (link.pathname !== window.location.pathname) return

    requestAnimationFrame(() => {
      this.scrollToVisibleTarget(link.hash, 'smooth')
    })
  }

  /**
   * @param {string} hash - The hash we're navigating to, including the leading #
   * @param {ScrollBehavior} behavior - How the corrective scroll should animate
   *
   * @returns {undefined}
   */
  scrollToVisibleTarget (hash, behavior) {
    const targets = this.getTargets(hash)

    // Nothing to correct: either no match, or only one copy exists and the
    // browser has already handled it.
    if (targets.length < 2) return

    const target = targets.find(this.isVisible)

    // The visible copy is the one the browser scrolled to anyway.
    if (!target || target === targets[0]) return

    target.scrollIntoView({ behavior, block: 'start' })
  }

  /**
   * Every element the hash resolves to, not just the first one like
   * `getElementById` would give us.
   *
   * @param {string} hash - The hash we're navigating to, including the leading #
   *
   * @returns {Array<Element>}
   */
  getTargets (hash) {
    if (!hash) return []

    const id = decodeURIComponent(hash.replace(/^#/, ''))

    if (!id) return []

    return getElements('[id], a[name]').filter(
      el => el.id === id || el.getAttribute('name') === id,
    )
  }

  /**
   * `display: none` elements generate no boxes, so they have no client rects.
   *
   * @param {Element} el
   *
   * @returns {boolean}
   */
  isVisible (el) {
    return el.getClientRects().length > 0
  }
}

window.BAO.AnchorScroll = new AnchorScroll()
window.BAO.AnchorScroll.init()
